import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Loading from '../../components/loading';
import ModalErrorMessage from '../../components/modalErrorMessage/modalErrorMessage';
import { useProfessionals } from '../../hooks/useProfessionals';
import { useRooms } from '../../hooks/useRooms';
import { useRoomLabel } from '../../hooks/useRoomLabel';
import { useSaveAgendaBlock } from '../hooks/useSaveAgendaBlock';
import { useDeleteAgendaBlock } from '../hooks/useDeleteAgendaBlock';
import { parseBlockDateTime } from './toBlockEvent';

const ALL_PROFESSIONALS_OPTION = '';
const ALL_ROOMS_OPTION = '';

function AgendaBlockModal({ open, onClose, onSave, onDelete, agendaBlock: selectedBlock }) {
  const navigate = useNavigate();
  const { professionals } = useProfessionals();
  const { rooms } = useRooms();
  const roomLabel = useRoomLabel();
  const { save: saveAgendaBlockRequest } = useSaveAgendaBlock();
  const { remove: deleteAgendaBlockRequest } = useDeleteAgendaBlock();

  const [isLoading, setLoading] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [professionalId, setProfessionalId] = useState(ALL_PROFESSIONALS_OPTION);
  const [roomId, setRoomId] = useState(ALL_ROOMS_OPTION);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [startTime, setStartTime] = useState(dayjs('08:00', 'HH:mm'));
  const [endTime, setEndTime] = useState(dayjs('18:00', 'HH:mm'));
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      const start = parseBlockDateTime(selectedBlock?.startDateTime);
      const end = parseBlockDateTime(selectedBlock?.endDateTime);

      setProfessionalId(selectedBlock?.professionalId ?? ALL_PROFESSIONALS_OPTION);
      setRoomId(selectedBlock?.roomId ?? ALL_ROOMS_OPTION);
      setStartDate(start ?? dayjs());
      setEndDate(end ?? dayjs());
      setStartTime(start ?? dayjs('08:00', 'HH:mm'));
      setEndTime(end ?? dayjs('18:00', 'HH:mm'));
      setReason(selectedBlock?.reason ?? '');
      setShowErrorMessage(false);
      setErrorMessage('');
      setDeleteConfirmOpen(false);
    }
  }, [selectedBlock, open]);

  const combineDateTime = (day, time) =>
    dayjs(day).hour(time.hour()).minute(time.minute()).second(0);

  // Bloqueio é por profissional OU por sala: escolher um limpa o outro.
  const handleProfessionalChange = (value) => {
    setProfessionalId(value);
    if (value) setRoomId(ALL_ROOMS_OPTION);
  };

  const handleRoomChange = (value) => {
    setRoomId(value);
    if (value) setProfessionalId(ALL_PROFESSIONALS_OPTION);
  };

  const saveAgendaBlock = () => {
    if (!startDate?.isValid() || !endDate?.isValid()) {
      setShowErrorMessage(true);
      setErrorMessage('Selecionar a data inicial e a data final é obrigatório.');
      return;
    }

    if (!startTime?.isValid() || !endTime?.isValid()) {
      setShowErrorMessage(true);
      setErrorMessage('Informe a hora inicial e a hora final do bloqueio.');
      return;
    }

    const start = combineDateTime(startDate, startTime);
    const end = combineDateTime(endDate, endTime);

    if (!end.isAfter(start)) {
      setShowErrorMessage(true);
      setErrorMessage('A data/hora final deve ser maior que a inicial.');
      return;
    }

    const input = {
      id: selectedBlock?.id,
      professionalId: professionalId || null,
      roomId: roomId || null,
      startDateTime: start.format('DD/MM/YYYY HH:mm:ss'),
      endDateTime: end.format('DD/MM/YYYY HH:mm:ss'),
      reason: reason.trim() || null,
    };

    setLoading(true);
    saveAgendaBlockRequest(input)
      .then((returnFromApi) => {
        setLoading(false);
        onClose();
        onSave(returnFromApi);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(String(returnFromApi.response?.data ?? 'Erro ao salvar o bloqueio.'));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  const handleConfirmDelete = () => {
    setLoading(true);
    deleteAgendaBlockRequest(selectedBlock.id)
      .then((returnFromApi) => {
        setLoading(false);
        setDeleteConfirmOpen(false);
        onClose();
        onDelete(returnFromApi);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setDeleteConfirmOpen(false);
        setShowErrorMessage(true);
        setErrorMessage(String(returnFromApi.response?.data ?? 'Erro ao excluir o bloqueio.'));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {selectedBlock?.id ? 'Editar Bloqueio' : 'Bloquear Agenda'}
      </DialogTitle>
      <DialogContent>
        <Loading isToHidden={isLoading === false} />
        {showErrorMessage && (
          <ModalErrorMessage message={errorMessage} setShow={setShowErrorMessage} />
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          Reserve um intervalo em que a agenda ficará indisponível (almoço, congresso, feriado,
          manutenção). Nenhuma consulta poderá ser marcada nesse período.
        </Typography>

        <Stack spacing={2}>
          <TextField
            id="professional"
            select
            label="Profissional"
            fullWidth
            value={professionalId}
            onChange={(e) => handleProfessionalChange(e.target.value)}
          >
            <MenuItem value={ALL_PROFESSIONALS_OPTION}>
              <em>Toda a clínica</em>
            </MenuItem>
            {professionals.map((professional) => (
              <MenuItem key={professional.id} value={professional.id}>
                {professional.nome || professional.name}
              </MenuItem>
            ))}
          </TextField>

          {rooms.length > 1 && (
            <TextField
              id="room"
              select
              label={roomLabel.singular}
              fullWidth
              value={roomId}
              onChange={(e) => handleRoomChange(e.target.value)}
            >
              <MenuItem value={ALL_ROOMS_OPTION}>
                <em>{roomLabel.allOption}</em>
              </MenuItem>
              {rooms.map((room) => (
                <MenuItem key={room.id} value={room.id}>
                  {room.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <DatePicker
              label="Data Inicial"
              value={startDate}
              onChange={(value) => setStartDate(value)}
              format="DD/MM/YYYY"
              slotProps={{ textField: { fullWidth: true } }}
            />
            <TimeField
              label="Hora Inicial"
              value={startTime}
              format="HH:mm"
              onChange={(value) => setStartTime(value)}
              sx={{ width: { xs: '100%', sm: 140 } }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <DatePicker
              label="Data Final"
              value={endDate}
              onChange={(value) => setEndDate(value)}
              format="DD/MM/YYYY"
              slotProps={{ textField: { fullWidth: true } }}
            />
            <TimeField
              label="Hora Final"
              value={endTime}
              format="HH:mm"
              onChange={(value) => setEndTime(value)}
              sx={{ width: { xs: '100%', sm: 140 } }}
            />
          </Stack>

          <TextField
            id="reason"
            label="Motivo"
            type="text"
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex.: Almoço, Congresso, Feriado"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '0 24px 16px' }}>
        {selectedBlock?.id && (
          <Button
            onClick={() => setDeleteConfirmOpen(true)}
            color="error"
            startIcon={<DeleteOutlineIcon />}
            sx={{ mr: 'auto' }}
          >
            Excluir
          </Button>
        )}
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={saveAgendaBlock} variant="contained" color="warning">
          {selectedBlock?.id ? 'Salvar' : 'Bloquear'}
        </Button>
      </DialogActions>

      <Dialog
        open={isDeleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Excluir bloqueio</DialogTitle>
        <DialogContent dividers>
          <Typography>
            O horário voltará a ficar disponível para agendamentos. Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

AgendaBlockModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  agendaBlock: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    professionalId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    roomId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    startDateTime: PropTypes.string,
    endDateTime: PropTypes.string,
    reason: PropTypes.string,
  }),
};

AgendaBlockModal.defaultProps = {
  onDelete: () => {},
  agendaBlock: null,
};

export default AgendaBlockModal;
